/** Úchyt pro resize mezi dvěma flex sourozenci. Tah přerozdělí jejich `basis` (%) přes resizeMath.
 *  Měří aktuální px sourozenců z DOM (prev/next sibling) + kontejneru; během tahu pauzne undo (1 krok). */

import React, { useRef } from "react";
import { findNode, type LayoutSize } from "@globals/layouts";
import { cn } from "@client/lib/cn";
import { useLayoutEditor } from "@client/store/layoutEditor";
import { resizePair } from "../resizeMath";

export const ResizeSplitter: React.FC<{ idA: string; idB: string; dir: "row" | "col" }> = ({ idA, idB, dir }) => {
  const ref = useRef<HTMLDivElement>(null);
  const setSize = useLayoutEditor((s) => s.setSize);
  const horizontal = dir === "row";

  const sizeOf = (id: string): LayoutSize => {
    const d = useLayoutEditor.getState().doc;
    return (d ? findNode(d.root, id)?.node.size : undefined) ?? {};
  };

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = ref.current;
    const slotA = el?.previousElementSibling as HTMLElement | null;
    const slotB = el?.nextElementSibling as HTMLElement | null;
    const container = el?.parentElement;
    if (!slotA || !slotB || !container) return;

    const start = horizontal ? e.clientX : e.clientY;
    const aPx0 = horizontal ? slotA.offsetWidth : slotA.offsetHeight;
    const bPx0 = horizontal ? slotB.offsetWidth : slotB.offsetHeight;
    const containerPx = horizontal ? container.clientWidth : container.clientHeight;
    const aSize0 = sizeOf(idA);
    const bSize0 = sizeOf(idB);

    const temporal = useLayoutEditor.temporal.getState();
    temporal.pause(); // celý tah = 1 undo krok

    const move = (ev: PointerEvent) => {
      const pos = horizontal ? ev.clientX : ev.clientY;
      const { aBasis, bBasis } = resizePair(aPx0, bPx0, pos - start, containerPx);
      // grow:0, ať basis (%) drží a nepřebije ho flex-grow
      setSize(idA, { ...aSize0, grow: 0, basis: aBasis });
      setSize(idB, { ...bSize0, grow: 0, basis: bBasis });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.userSelect = "";
      temporal.resume();
    };
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onClick={(e) => e.stopPropagation()}
      title="Táhni pro změnu velikosti"
      className={cn(
        "shrink-0 self-stretch rounded bg-border/40 transition-colors hover:bg-primary/60",
        horizontal ? "w-1.5 cursor-col-resize" : "h-1.5 cursor-row-resize"
      )}
    />
  );
};

export default ResizeSplitter;
